import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { UndrMark, UndrWordmark } from "../brand/UndrMark";
import { IconArrow, IconFair, IconQuality, IconRank } from "../components/Icons";
import { Skeleton } from "../components/ui";
import { safeGet } from "../lib/api";
import { fmtDate, int, isNum, pct } from "../lib/format";
import { useTheme } from "../theme/ThemeContext";
import { THEME_NAMES } from "../theme/tokens";

function asPct(v) {
  if (!isNum(v)) return null;
  return Math.abs(v) <= 1.5 && v !== 0 ? v * 100 : v;
}
function metric(s, side, names) {
  const nested = s?.[side] || s?.[`${side}_metrics`] || s?.metrics?.[side];
  for (const n of names) {
    if (nested && nested[n] != null) return nested[n];
    if (s?.[`${side}_${n}`] != null) return s[`${side}_${n}`];
  }
  return null;
}

export default function Landing() {
  const theme = useTheme();
  const [teaser, setTeaser] = useState(undefined);
  const [bt, setBt] = useState(undefined);

  useEffect(() => {
    safeGet("/api/public/teaser", null).then((t) => setTeaser(t && typeof t === "object" ? t : null));
    safeGet("/api/backtest/latest", null).then((s) => setBt(s && typeof s === "object" && Object.keys(s).length ? s : null));
  }, []);

  const btm = useMemo(() => {
    if (!bt) return null;
    return {
      pc: asPct(metric(bt, "portfolio", ["cagr", "cagr_pct"])),
      bc: asPct(metric(bt, "benchmark", ["cagr", "cagr_pct"])),
      pm: asPct(metric(bt, "portfolio", ["max_drawdown", "mdd", "max_drawdown_pct"])),
      bm: asPct(metric(bt, "benchmark", ["max_drawdown", "mdd", "max_drawdown_pct"])),
    };
  }, [bt]);

  const nextTheme = THEME_NAMES[(THEME_NAMES.indexOf(theme.theme) + 1) % THEME_NAMES.length];

  return (
    <div className="landing">
      <div className="wrap">
        <nav className="landing-nav" aria-label="Site">
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", color: "inherit" }} aria-label="Undr home">
            <UndrMark size={30} />
            <UndrWordmark size={24} />
          </Link>
          <div className="links">
            <a href="#how">How it works</a>
            <a href="#markets">Markets</a>
            <a href="#backtest">Backtest</a>
            <button type="button" className="btn sm" onClick={() => theme.setTheme(nextTheme)} aria-label={`Switch theme to ${nextTheme}`} title={`Theme: ${theme.theme}`}>
              {theme.theme}
            </button>
            <Link className="btn primary sm" to="/app">Open app</Link>
          </div>
        </nav>

        <section className="hero">
          <h1>Great companies. Under fair value.</h1>
          <p className="lead">
            Undr scores every company in the S&amp;P 500, Nifty 50 and Hang Seng on a 0–10 quality scale built from multi-year
            financial statements, prices each one with a 10-year payback fair value, and rebalances an equal-weight portfolio of
            the best names every quarter.
          </p>
          <div className="ctas">
            <Link className="btn primary" to="/app">
              Open app <IconArrow />
            </Link>
            <a className="btn" href="#how">How it works</a>
          </div>
          <div className="teaser" aria-live="polite">
            {teaser === undefined && (
              <>
                <Skeleton width={120} height={14} />
                <Skeleton width={90} height={14} />
                <Skeleton width={140} height={14} />
              </>
            )}
            {teaser === null && <span>Live figures appear here once the first scan has run.</span>}
            {teaser && (
              <>
                <span>
                  Today: <strong className="num">{int(teaser.buy_count)} BUY</strong> of <span className="num">{int(teaser.scored)}</span>
                </span>
                <span aria-hidden="true">·</span>
                <span>
                  avg quality <strong className="num">{isNum(teaser.avg_quality) ? teaser.avg_quality.toFixed(1) : "—"}</strong>
                </span>
                {isNum(teaser.below_fair) && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>
                      <strong className="num">{int(teaser.below_fair)}</strong> below fair value
                    </span>
                  </>
                )}
                <span aria-hidden="true">·</span>
                <span>last scan {fmtDate(teaser.last_scan)}</span>
                {teaser.markets && (
                  <span style={{ marginLeft: "auto", display: "flex", gap: 10 }} className="num">
                    {Object.entries(teaser.markets).map(([k, v]) => (
                      <span key={k}>
                        {k === "INDIA" ? "India" : k === "CHINA" ? "China" : k} {int(v?.buy)}/{int(v?.scored)}
                      </span>
                    ))}
                  </span>
                )}
              </>
            )}
          </div>
        </section>

        <section className="section" id="how">
          <h2>How it works</h2>
          <p className="sub">Three steps, run every weekday after each market closes. No opinions, only statements and prices.</p>
          <div className="steps">
            <div className="step">
              <div className="icon"><IconQuality /></div>
              <span className="k">1 · Quality</span>
              <h3>Score 0–10 from the statements</h3>
              <p>
                Seven dimensions — consistency, growth, margin, ROE, debt, wealth and recent momentum — computed from every fiscal
                year Yahoo Finance serves (about five) of income statements, balance sheets and cash flows. Weighted, then capped by data availability.
              </p>
            </div>
            <div className="step">
              <div className="icon"><IconFair /></div>
              <span className="k">2 · Fair value</span>
              <h3>10-year payback price</h3>
              <p>
                How much would you pay to get your money back from owner earnings in ten years, with growth capped and
                haircut, plus net cash? That price is the fair value. Under it, with quality ≥ 7, is a BUY.
              </p>
            </div>
            <div className="step">
              <div className="icon"><IconRank /></div>
              <span className="k">3 · Rank &amp; rebalance</span>
              <h3>Equal weight, every quarter</h3>
              <p>
                A rank score blends quality, discount to fair value and momentum. The top N per market form an equal-weight
                portfolio; holdings inside the keep band survive to avoid churn, the rest rotate each calendar quarter.
              </p>
            </div>
          </div>
        </section>

        <section className="section" id="markets">
          <h2>Markets</h2>
          <p className="sub">Three index universes, refreshed from public constituent lists every week.</p>
          <div className="markets">
            <div className="market">
              <span className="name">US</span>
              <span className="idx">S&amp;P 500 · benchmark SPY</span>
              <span className="num" style={{ fontSize: 12, color: "var(--muted)" }}>USD · statements lag 90 days</span>
            </div>
            <div className="market">
              <span className="name">India</span>
              <span className="idx">Nifty 50 · benchmark ^NSEI</span>
              <span className="num" style={{ fontSize: 12, color: "var(--muted)" }}>INR · statements lag 75 days</span>
            </div>
            <div className="market">
              <span className="name">Hong Kong</span>
              <span className="idx">Hang Seng · benchmark ^HSI</span>
              <span className="num" style={{ fontSize: 12, color: "var(--muted)" }}>HKD · statements lag 100 days</span>
            </div>
          </div>
        </section>

        <section className="section" id="backtest">
          <h2>Backtest</h2>
          <p className="sub">Quarterly rebalance of the rule set, compared with buying the index.</p>
          {bt === undefined && (
            <div className="markets"><div className="market"><Skeleton height={28} width="50%" /></div><div className="market"><Skeleton height={28} width="50%" /></div><div className="market"><Skeleton height={28} width="50%" /></div></div>
          )}
          {bt === null && <div className="disclaimer">No backtest has been published yet.</div>}
          {bt && btm && (
            <div className="markets" style={{ marginBottom: 16 }}>
              <div className="market">
                <span className="idx">{bt.market || "Market"}{bt.top_n ? ` · top ${bt.top_n}` : ""}</span>
                <span className="name num">{bt.window || [bt.start || bt.from, bt.end || bt.to].filter(Boolean).join(" → ") || "—"}</span>
                <span className="idx">{isNum(bt.n_periods) ? bt.n_periods : Array.isArray(bt.periods) ? bt.periods.length : "—"} rebalance periods</span>
              </div>
              <div className="market">
                <span className="idx">CAGR · portfolio vs benchmark</span>
                <span className="name num" style={{ color: isNum(btm.pc) && btm.pc >= (btm.bc ?? 0) ? "var(--good)" : "var(--ink)" }}>
                  {pct(btm.pc, { digits: 1 })} <span style={{ color: "var(--muted)", fontWeight: 400 }}>vs {pct(btm.bc, { digits: 1 })}</span>
                </span>
                <span className="idx">annualised, no costs</span>
              </div>
              <div className="market">
                <span className="idx">Max drawdown · portfolio vs benchmark</span>
                <span className="name num">
                  {pct(btm.pm, { sign: false, digits: 1 })} <span style={{ color: "var(--muted)", fontWeight: 400 }}>vs {pct(btm.bm, { sign: false, digits: 1 })}</span>
                </span>
                <span className="idx">peak to trough</span>
              </div>
            </div>
          )}
          <div className="disclaimer">
            Backtests are simulated with today&apos;s index membership, no transaction costs and statement lags as a point-in-time
            guard; real results would differ. Nothing on this site is investment advice or a recommendation to buy or sell any
            security. Do your own research.
          </div>
        </section>

        <footer className="landing-footer">
          <div className="links">
            <a href="https://github.com/" target="_blank" rel="noreferrer">GitHub</a>
            <a href="https://telegram.org/" target="_blank" rel="noreferrer">Telegram</a>
            <Link to="/app">Open app</Link>
          </div>
          <span>
            Undr is a personal research tool. Data from public filings and market feeds may be delayed or wrong. Not investment
            advice.
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <UndrMark size={16} /> © {new Date().getFullYear()} Undr
          </span>
        </footer>
      </div>
    </div>
  );
}
